import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  fetchClients, fetchInvoices, createInvoice, updateInvoice, deleteInvoice
} from '../models/InvoiceService';
import { useAuth } from '../models/AuthContext';
import { toDate, parsePrice, invoiceTotal, itemLabel, isPaid } from '../models/urssaf';
import PageShell, { Btn, Pill, fmtEUR } from '../components/design/PageShell';
import ClientFilter from '../components/design/ClientFilter';
import { Ic } from '../components/design/Icons';
import GridProtocolPDF from '../components/GridProtocolPDF';
import SchedulePDF from '../components/SchedulePDF';
import { BlobProvider } from '@react-pdf/renderer';

const clientNameOf = (doc) =>
  (typeof doc.client === 'string' ? doc.client : (doc.client?.Title || doc.client?.name)) || '—';

/** Date d'ancrage pour le tri : prestation si connue, sinon émission. */
const sortDate = (inv) => toDate(inv.prestaDate) || toDate(inv.created) || new Date(0);

/** Date -> valeur d'un <input type="date"> en heure locale. */
const dateInputValue = (v, fallbackToToday = false) => {
  const d = toDate(v) || (fallbackToToday ? new Date() : null);
  if (!d) return '';
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

const fmtDate = (d) => {
  const date = toDate(d);
  if (!date) return '—';
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// Sanitize a string for use in a filename
const slugify = (s) => (s || '')
  .normalize('NFD').replace(/[̀-ͯ]/g, '') // strip accents
  .replace(/[^a-zA-Z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 40);

const buildPdfFileName = (label, prefix, doc, clientName) => {
  const date = toDate(doc.created) || new Date();
  const year = date.getFullYear();
  const numStr = String(doc.num).padStart(4, '0');
  const client = slugify(clientName);
  return `${label}-${prefix}-${year}-${numStr}${client ? '-' + client : ''}.pdf`;
};

// Force a real download (not an open-in-tab). Programmatic anchor click with download attr.
const forceDownload = (url, fileName) => {
  if (!url) return;
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { document.body.removeChild(a); }, 100);
  } catch (e) {
    console.error('Download error', e);
    // fallback: open in new tab
    window.open(url, '_blank');
  }
};

const EMITTER = {
  name: "Matthieu SIMAR",
  address: "2550 Lignerolles",
  city: "27160 Breteuil-sur-Iton",
  email: "matthieu.simar@gmail.com",
  siret: "753 456 102 00023",
  siren: "753 456 102",
};

const Invoices = () => {
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [tab, setTab] = useState('actuelles');
  const [search, setSearch] = useState('');
  const [clientFilter, setClientFilter] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);

  const { currentUser } = useAuth();
  const aliveRef = useRef(true);
  useEffect(() => () => { aliveRef.current = false; }, []);

  /**
   * Chargement résilient : `allSettled` évite qu'une collection en échec
   * (trimestres, devis…) vide toute la page, ce qui obligeait à passer par
   * Devis puis revenir sur Factures pour voir la liste.
   * Renvoie true si les factures ont bien été récupérées.
   */
  const loadAll = useCallback(async () => {
    const results = await Promise.allSettled([fetchInvoices(), fetchClients()]);
    if (!aliveRef.current) return true;
    const [inv, cl] = results;
    if (inv.status === 'fulfilled') setInvoices(inv.value);
    if (cl.status === 'fulfilled') setClients(cl.value);

    results.filter(r => r.status === 'rejected')
      .forEach(r => console.error('[Factures] chargement partiel:', r.reason));

    return inv.status === 'fulfilled';
  }, []);

  useEffect(() => {
    let cancelled = false;
    let attempt = 0;

    const run = async () => {
      const ok = await loadAll();
      if (cancelled || !aliveRef.current) return;
      if (ok) {
        setLoadError(null);
        setLoading(false);
        return;
      }
      // Nouvelle tentative : la toute première requête Firestore peut échouer
      // tant que la session d'authentification n'est pas complètement établie.
      if (attempt < 2) {
        attempt += 1;
        setTimeout(() => { if (!cancelled) run(); }, 600 * attempt);
        return;
      }
      setLoadError('Impossible de charger les factures.');
      setLoading(false);
    };

    setLoading(true);
    run();
    return () => { cancelled = true; };
  }, [loadAll, currentUser?.uid]);

  const retry = () => {
    setLoading(true);
    setLoadError(null);
    loadAll().then((ok) => {
      if (!aliveRef.current) return;
      if (!ok) setLoadError('Impossible de charger les factures.');
      setLoading(false);
    });
  };

  const stats = useMemo(() => {
    const now = new Date();
    const q = Math.floor(now.getMonth() / 3);
    const qS = new Date(now.getFullYear(), q * 3, 1);
    const qE = new Date(now.getFullYear(), q * 3 + 3, 0, 23, 59, 59);

    const thisQ = invoices.filter(i => {
      const d = toDate(i.created);
      return d && d >= qS && d <= qE;
    });
    const total = thisQ.reduce((a, i) => a + invoiceTotal(i), 0);
    const paid = thisQ.filter(isPaid);
    const pending = thisQ.filter(i => !isPaid(i));
    const avg = thisQ.length ? total / thisQ.length : 0;
    const paidTotal = paid.reduce((a, i) => a + invoiceTotal(i), 0);
    const pendingTotal = pending.reduce((a, i) => a + invoiceTotal(i), 0);
    return { count: thisQ.length, total, avg, paid: paid.length, pending: pending.length, paidTotal, pendingTotal };
  }, [invoices]);

  /** Clients ayant au moins une facture, les plus récents en tête. */
  const clientOptions = useMemo(() => {
    const map = new Map();
    invoices.forEach(i => {
      const name = clientNameOf(i);
      const d = sortDate(i);
      const cur = map.get(name);
      if (cur) {
        cur.count += 1;
        if (d > cur.last) cur.last = d;
      } else {
        map.set(name, { name, count: 1, last: d });
      }
    });
    return [...map.values()].sort((a, b) => b.last - a.last);
  }, [invoices]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = invoices.filter(i => {
      if (clientFilter && clientNameOf(i) !== clientFilter) return false;
      if (!q) return true;
      return clientNameOf(i).toLowerCase().includes(q) || String(i.num).includes(q);
    });

    // Les plus récentes en haut.
    const byRecent = (a, b) => sortDate(b) - sortDate(a);

    if (tab === 'a-venir') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return list
        .filter(i => {
          const d = toDate(i.prestaDate);
          return d && d > today;
        })
        .sort((a, b) => toDate(a.prestaDate) - toDate(b.prestaDate)); // échéance la plus proche en tête
    }

    if (tab === 'par-client') {
      const groups = new Map();
      list.forEach(i => {
        const name = clientNameOf(i);
        if (!groups.has(name)) groups.set(name, []);
        groups.get(name).push(i);
      });
      return [...groups.entries()]
        .map(([name, docs]) => [name, [...docs].sort(byRecent)])
        .sort((a, b) => sortDate(b[1][0]) - sortDate(a[1][0]));
    }

    return [...list].sort(byRecent);
  }, [invoices, tab, search, clientFilter]);

  const handleDelete = async (inv) => {
    if (!window.confirm(`Supprimer la facture #${inv.num} ?`)) return;
    try {
      await deleteInvoice(inv.id);
      setInvoices(invoices.filter(i => i.id !== inv.id));
    } catch (e) { console.error(e); }
  };

  // Crée une facture par mois entre les deux dates puis génère le PDF d'échéancier
  const handleScheduleSubmit = async ({ clientId, clientName, title, price, start, end }) => {
    const clientData = clients.find(c => c.id === clientId)
      || { id: clientId, Title: clientName, name: clientName };
    const startDate = new Date(start);
    const endDate = new Date(end);

    const lastNum = invoices.reduce((max, i) => Math.max(max, i.num || 0), 0);
    let currentNum = lastNum + 1;

    const invoicesToCreate = [];
    for (let d = new Date(startDate); d <= endDate; d.setMonth(d.getMonth() + 1)) {
      const lastDayOfMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0);
      invoicesToCreate.push({
        client: clientData.Title || clientName,
        clientId: clientData.id,
        created: new Date(),
        prestaDate: lastDayOfMonth,
        num: currentNum,
        products: [{ title, description: '', quantity: 1, unitPrice: price, isMerchandise: false }],
      });
      currentNum++;
    }

    if (invoicesToCreate.length === 0) {
      alert('Aucune échéance à créer : vérifiez les dates de début et de fin.');
      return;
    }

    try {
      for (const inv of invoicesToCreate) {
        await createInvoice(inv);
      }
      const refreshed = await fetchInvoices();
      setInvoices(refreshed);
      setScheduleOpen(false);

      const scheduleInvoice = {
        num: `${invoicesToCreate[0].num}-${invoicesToCreate[invoicesToCreate.length - 1].num}`,
        products: invoicesToCreate.map(inv => ({
          title: `${title} - ${inv.prestaDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}`,
          description: `Facture N° ${inv.num}`,
          quantity: 1,
          unitPrice: price,
        })),
      };
      const percentages = invoicesToCreate.map(() => Math.round(10000 / invoicesToCreate.length) / 100);
      SchedulePDF({ invoice: scheduleInvoice, client: clientData, emitter: EMITTER, percentages }).downloadPDF();
    } catch (e) {
      console.error('Erreur lors de la génération de l\'échéancier:', e);
      alert('Erreur lors de la génération de l\'échéancier. Veuillez réessayer.');
    }
  };

  return (
    <PageShell
      section="factures"
      number="02"
      title="Factures"
      subtitle="Émission, suivi et archivage des factures clients"
      actions={
        <>
          <Btn kind="ghost" icon="cal" onClick={() => setScheduleOpen(true)}>Échéancier</Btn>
          <Btn kind="primary" icon="plus" onClick={() => setFormOpen(true)}>Nouvelle facture</Btn>
        </>
      }
    >
      <div className="ta-filters">
        <div className="ta-search">
          <span className="ta-search-ic">{Ic.search}</span>
          <input
            placeholder="Rechercher par client, numéro, montant…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="ta-tabs">
          {[
            ['actuelles', 'actuelles'],
            ['a-venir', 'à venir'],
            ['par-client', 'par client'],
          ].map(([k, l]) => (
            <button key={k} className={`ta-tab ${tab === k ? 'is-active' : ''}`} onClick={() => setTab(k)}>{l}</button>
          ))}
        </div>
      </div>

      <ClientFilter
        clients={clientOptions}
        value={clientFilter}
        onChange={setClientFilter}
        total={invoices.length}
      />

      {loadError && (
        <div className="ta-callout ta-callout-warn" style={{ alignItems: 'center' }}>
          <span style={{ flex: 1 }}>{loadError}</span>
          <Btn kind="ghost" className="ta-btn-sm" onClick={retry}>Réessayer</Btn>
        </div>
      )}

      <section className="ta-stats">
        <div>
          <div className="eyebrow">{"// "}Émises (T en cours)</div>
          <div className="f">{stats.count}</div>
          <div className="d tnum">{fmtEUR(stats.total)} €</div>
        </div>
        <div>
          <div className="eyebrow">{"// "}En attente</div>
          <div className="f" style={{ color: 'var(--warn)' }}>{stats.pending}</div>
          <div className="d tnum">{fmtEUR(stats.pendingTotal)} €</div>
        </div>
        <div>
          <div className="eyebrow">{"// "}Payées</div>
          <div className="f" style={{ color: 'var(--ok)' }}>{stats.paid}</div>
          <div className="d tnum">{fmtEUR(stats.paidTotal)} €</div>
        </div>
        <div>
          <div className="eyebrow">{"// "}Moyenne</div>
          <div className="f">{Math.round(stats.avg)}</div>
          <div className="d tnum">€ / facture</div>
        </div>
      </section>

      {loading && <div className="mono" style={{ color: 'var(--ink-3)', padding: 24 }}>Chargement…</div>}

      {!loading && tab !== 'par-client' && (
        <section>
          <div className="ta-thead" style={{ gridTemplateColumns: '70px 1fr 120px 140px 160px 40px' }}>
            <div>N°</div>
            <div>Client</div>
            <div>Date</div>
            <div>Statut</div>
            <div style={{ textAlign: 'right' }}>Montant</div>
            <div></div>
          </div>
          {filtered.length === 0 && (
            <div className="ta-trow" style={{ gridTemplateColumns: '1fr', color: 'var(--ink-3)' }}>
              <div>{clientFilter ? `Aucune facture pour ${clientFilter}` : 'Aucune facture'}</div>
            </div>
          )}
          {filtered.map(inv => {
            const paid = isPaid(inv);
            const clientName = clientNameOf(inv);
            return (
              <div
                key={inv.id}
                className="ta-trow"
                style={{ gridTemplateColumns: '70px 1fr 120px 140px 160px 40px' }}
                onClick={() => setSelectedInvoice(inv)}
              >
                <div className="mono" style={{ color: 'var(--ink-3)' }}>#{inv.num}</div>
                <div>
                  <div style={{ fontSize: 13.5, letterSpacing: '-0.005em' }}>{clientName}</div>
                  <div className="mono" style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 3 }}>
                    INV-{toDate(inv.created)?.getFullYear() || '—'}-{String(inv.num).padStart(4, '0')}
                  </div>
                </div>
                <div className="mono tnum" style={{ color: 'var(--ink-2)', fontSize: 12 }}>
                  {fmtDate(inv.prestaDate || inv.created)}
                </div>
                <div>
                  <Pill tone={paid ? 'ok' : 'warn'}>{paid ? 'payée' : 'en attente'}</Pill>
                </div>
                <div className="mono tnum" style={{ textAlign: 'right', fontSize: 14, fontWeight: 500 }}>
                  {fmtEUR(invoiceTotal(inv))} €
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span className="mono" style={{ color: 'var(--ink-4)' }}>→</span>
                </div>
              </div>
            );
          })}
        </section>
      )}

      {!loading && tab === 'par-client' && (
        <section>
          {filtered.length === 0 && <div className="mono" style={{ color: 'var(--ink-3)' }}>Aucun client</div>}
          {filtered.map(([client, list]) => {
            const total = list.reduce((a, i) => a + invoiceTotal(i), 0);
            return (
              <div key={client} style={{ marginBottom: 40 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '16px 0', borderBottom: '1px solid var(--rule-strong)' }}>
                  <div style={{ fontSize: 18, fontWeight: 500, letterSpacing: '-0.015em' }}>{client}</div>
                  <div className="mono tnum" style={{ color: 'var(--accent)', fontSize: 16 }}>{fmtEUR(total)} €</div>
                </div>
                {list.map(inv => {
                  const paid = isPaid(inv);
                  return (
                    <div
                      key={inv.id}
                      className="ta-trow"
                      style={{ gridTemplateColumns: '70px 1fr 120px 140px 160px', fontSize: 13 }}
                      onClick={() => setSelectedInvoice(inv)}
                    >
                      <div className="mono" style={{ color: 'var(--ink-3)' }}>#{inv.num}</div>
                      <div className="mono" style={{ color: 'var(--ink-4)', fontSize: 11 }}>
                        INV-{toDate(inv.created)?.getFullYear() || '—'}-{String(inv.num).padStart(4, '0')}
                      </div>
                      <div className="mono tnum" style={{ color: 'var(--ink-2)', fontSize: 12 }}>{fmtDate(inv.prestaDate || inv.created)}</div>
                      <div><Pill tone={paid ? 'ok' : 'warn'}>{paid ? 'payée' : 'attente'}</Pill></div>
                      <div className="mono tnum" style={{ textAlign: 'right', fontWeight: 500 }}>{fmtEUR(invoiceTotal(inv))} €</div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </section>
      )}

      {/* Detail drawer / modal */}
      {selectedInvoice && (
        <InvoiceDrawer
          invoice={selectedInvoice}
          clients={clients}
          onClose={() => setSelectedInvoice(null)}
          onDelete={handleDelete}
          onEdit={(inv) => { setEditingInvoice(inv); setSelectedInvoice(null); }}
          onUpdate={async (patch) => {
            try {
              await updateInvoice(selectedInvoice.id, patch);
              setInvoices(prev => prev.map(i => i.id === selectedInvoice.id ? { ...i, ...patch } : i));
              setSelectedInvoice(prev => ({ ...prev, ...patch }));
            } catch (e) { console.error(e); alert('Erreur lors de la mise à jour'); }
          }}
        />
      )}

      {formOpen && (
        <InvoiceForm
          clients={clients}
          nextNum={(invoices.reduce((max, i) => Math.max(max, i.num || 0), 0)) + 1}
          onClose={() => setFormOpen(false)}
          onSave={async (payload) => {
            try {
              const id = await createInvoice(payload);
              setInvoices([...invoices, { id, ...payload }]);
              setFormOpen(false);
            } catch (e) { console.error(e); alert('Erreur lors de la création'); }
          }}
        />
      )}

      {editingInvoice && (
        <InvoiceForm
          clients={clients}
          invoice={editingInvoice}
          onClose={() => setEditingInvoice(null)}
          onSave={async (payload) => {
            try {
              await updateInvoice(editingInvoice.id, payload);
              const updated = { ...editingInvoice, ...payload };
              setInvoices(prev => prev.map(i => (i.id === editingInvoice.id ? updated : i)));
              setEditingInvoice(null);
              setSelectedInvoice(updated);
            } catch (e) { console.error(e); alert('Erreur lors de l\'enregistrement'); }
          }}
        />
      )}

      {scheduleOpen && (
        <ScheduleForm
          clients={clients}
          onClose={() => setScheduleOpen(false)}
          onSubmit={handleScheduleSubmit}
        />
      )}
    </PageShell>
  );
};

// --------- Drawer (detail) ---------
function InvoiceDrawer({ invoice, clients, onClose, onDelete, onUpdate, onEdit }) {
  const [pdfReady, setPdfReady] = useState(false);
  const paid = isPaid(invoice);
  const clientName = clientNameOf(invoice);
  const total = invoiceTotal(invoice);
  const paidInput = dateInputValue(invoice.paidDate);
  // Resolve full client object for the PDF
  const clientObj = invoice.clientData ||
    (typeof invoice.client === 'object' && invoice.client !== null ? invoice.client : null) ||
    (clients || []).find(c => c.id === invoice.clientId) ||
    (clients || []).find(c => (c.Title || c.name) === clientName) ||
    { Title: clientName, name: clientName, address: '', city: '', email: '' };

  return (
    <div className="ta-drawer-scrim" onClick={onClose}>
      <div className="ta-drawer-panel" onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 32 }}>
          <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: '0.2em' }}>
            FACTURE #{invoice.num}
          </div>
          <button onClick={onClose} className="ta-icon-btn" aria-label="Fermer">{Ic.close}</button>
        </div>

        <div style={{ fontSize: 32, letterSpacing: '-0.02em', marginBottom: 8 }}>{clientName}</div>
        <div className="mono" style={{ color: 'var(--ink-3)', fontSize: 12, letterSpacing: '0.08em', marginBottom: 32 }}>
          Émise le {fmtDate(invoice.created)} · prestation {fmtDate(invoice.prestaDate)}
        </div>

        <div style={{ border: '1px solid var(--rule)', borderRadius: 4, padding: 24, marginBottom: 32 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <Pill tone={paid ? 'ok' : 'warn'}>{paid ? 'payée' : 'en attente'}</Pill>
            <div className="mono tnum" style={{ fontSize: 36, color: 'var(--accent)' }}>{fmtEUR(total)} €</div>
          </div>
          {paid && (
            <div style={{
              marginTop: 20, paddingTop: 18, borderTop: '1px solid var(--rule)',
              display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
            }}>
              <span className="mono" style={{ fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>
                Encaissée le
              </span>
              <input
                type="date"
                value={paidInput}
                onChange={(e) => onUpdate({ paidDate: e.target.value ? new Date(`${e.target.value}T12:00:00`) : null })}
                style={{
                  padding: '8px 10px', background: 'var(--bg-1)', color: 'var(--ink)',
                  border: '1px solid var(--rule-2)', borderRadius: 3, fontFamily: 'var(--font-mono)', fontSize: 12,
                }}
              />
              <span className="mono" style={{ fontSize: 10.5, color: 'var(--ink-4)' }}>
                détermine le trimestre URSSAF
              </span>
            </div>
          )}
        </div>

        <div className="eyebrow" style={{ marginBottom: 16 }}>§ Prestations</div>
        <div style={{ borderTop: '1px solid var(--rule)' }}>
          {(invoice.products || []).map((p, i) => (
            <div key={i} style={{
              display: 'grid', gridTemplateColumns: '40px 1fr 60px 100px 120px',
              gap: 16, padding: '16px 0', borderBottom: '1px solid var(--rule)',
              alignItems: 'start',
            }}>
              <div className="mono" style={{ color: 'var(--ink-4)' }}>{String(i + 1).padStart(2, '0')}</div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{itemLabel(p) || <span style={{ color: 'var(--ink-4)' }}>— sans libellé —</span>}</div>
                {p.description && <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 4 }}>{p.description}</div>}
                {p.isMerchandise && (
                  <div className="mono" style={{ fontSize: 9.5, color: 'var(--ink-4)', marginTop: 5, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
                    marchandise
                  </div>
                )}
              </div>
              <div className="mono tnum" style={{ textAlign: 'right' }}>{p.quantity}</div>
              <div className="mono tnum" style={{ textAlign: 'right' }}>{fmtEUR(parsePrice(p.unitPrice))}</div>
              <div className="mono tnum" style={{ textAlign: 'right', color: 'var(--accent)' }}>
                {fmtEUR((Number(p.quantity) || 0) * parsePrice(p.unitPrice))} €
              </div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 48, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {!pdfReady && (
            <Btn kind="accent" icon="download" onClick={() => setPdfReady(true)}>Préparer le PDF</Btn>
          )}
          {pdfReady && (
            <BlobProvider document={<GridProtocolPDF invoice={invoice} client={clientObj} emitter={EMITTER} type="invoice" />}>
              {({ url, loading, error }) => {
                const fileName = buildPdfFileName('Facture', 'INV', invoice, clientName);
                return (
                  <a
                    href={url || '#'}
                    download={fileName}
                    className="ta-btn ta-btn-accent"
                    style={{ opacity: loading || error ? 0.5 : 1, pointerEvents: loading || error ? 'none' : 'auto' }}
                    onClick={(e) => {
                      if (loading || error) { e.preventDefault(); return; }
                      // Force download via dynamic anchor to ensure it's saved (not opened in tab)
                      e.preventDefault();
                      forceDownload(url, fileName);
                    }}
                  >
                    <span className="ta-btn-ic">{Ic.download}</span>
                    <span>{error ? 'Erreur PDF' : loading ? 'Génération…' : 'Télécharger PDF'}</span>
                  </a>
                );
              }}
            </BlobProvider>
          )}
          <Btn
            kind={paid ? 'ghost' : 'primary'}
            icon={paid ? undefined : 'check'}
            onClick={() => onUpdate(paid
              ? { paid: false, paidDate: null }
              : { paid: true, paidDate: new Date() })}
          >
            {paid ? 'Marquer en attente' : 'Marquer payée'}
          </Btn>
          <Btn kind="ghost" icon="doc" onClick={() => onEdit(invoice)}>Modifier</Btn>
          <Btn kind="danger" onClick={() => { onDelete(invoice); onClose(); }}>Supprimer</Btn>
        </div>
      </div>
    </div>
  );
}

// --------- Create / Edit Form ---------
const LINE_COLS = '36px 1fr 84px 56px 96px 100px 28px';

/** Lignes d'un document existant remises au format du formulaire. */
const toFormLines = (doc) => {
  const lines = (doc?.products || []).map(p => ({
    title: itemLabel(p),
    description: p.description || '',
    quantity: Number(p.quantity) || 0,
    unitPrice: parsePrice(p.unitPrice),
    isMerchandise: !!p.isMerchandise,
  }));
  return lines.length ? lines : [{ title: '', description: '', quantity: 1, unitPrice: 0, isMerchandise: false }];
};

function InvoiceForm({ clients, nextNum, invoice, onClose, onSave }) {
  const isEdit = !!invoice;
  const [clientId, setClientId] = useState(invoice?.clientId || '');
  const [clientName, setClientName] = useState(invoice ? clientNameOf(invoice) : '');
  const [num, setNum] = useState(isEdit ? (invoice.num ?? nextNum) : nextNum);
  const [prestaDate, setPrestaDate] = useState(
    isEdit ? dateInputValue(invoice.prestaDate, true) : dateInputValue(new Date())
  );
  const [products, setProducts] = useState(() => toFormLines(invoice));
  const [saving, setSaving] = useState(false);
  const total = products.reduce((a, p) => a + (Number(p.quantity) || 0) * parsePrice(p.unitPrice), 0);

  const updateP = (i, field, value) => {
    setProducts(prev => prev.map((p, j) => (j === i ? { ...p, [field]: value } : p)));
  };

  const submit = async () => {
    if (!clientId && !clientName) { alert('Sélectionner un client'); return; }
    const lines = products
      .filter(p => p.title && p.title.trim())
      .map(p => ({
        title: p.title.trim(),
        description: p.description || '',
        quantity: Number(p.quantity) || 0,
        unitPrice: parsePrice(p.unitPrice),
        isMerchandise: !!p.isMerchandise,
      }));
    if (lines.length === 0) { alert('Ajouter au moins une ligne avec un libellé'); return; }

    const payload = {
      client: clientName,
      clientId,
      prestaDate: new Date(`${prestaDate}T12:00:00`),
      num: Number(num) || 0,
      products: lines,
    };
    // On ne touche pas à la date d'émission d'une facture déjà émise.
    if (!isEdit) payload.created = new Date();

    setSaving(true);
    try {
      await onSave(payload);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ta-drawer-scrim" onClick={onClose}>
      <div className="ta-drawer-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 820 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 32 }}>
          <div>
            <div className="eyebrow" style={{ color: 'var(--accent)' }}>
              § 02.A · {isEdit ? 'Modifier la facture' : 'Nouvelle facture'}
            </div>
            <div style={{ fontSize: 32, letterSpacing: '-0.02em', marginTop: 8, display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span>{isEdit ? 'Facture' : 'Brouillon'} #</span>
              <input
                type="number"
                value={num}
                onChange={(e) => setNum(e.target.value)}
                aria-label="Numéro de facture"
                style={{
                  width: 110, fontSize: 32, letterSpacing: '-0.02em',
                  background: 'transparent', color: 'var(--ink)',
                  border: 0, borderBottom: '1px dashed var(--rule-2)',
                  fontFamily: 'var(--font-mono)', outline: 'none', padding: 0,
                }}
              />
            </div>
            {isEdit && (
              <div className="mono" style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 10, letterSpacing: '0.08em' }}>
                émise le {fmtDate(invoice.created)}
              </div>
            )}
          </div>
          <button onClick={onClose} className="ta-icon-btn" aria-label="Fermer">{Ic.close}</button>
        </div>

        <div className="eyebrow" style={{ marginBottom: 16 }}>§ 01 · Client</div>
        <select
          value={clientId}
          onChange={(e) => {
            const id = e.target.value;
            const c = clients.find(x => x.id === id);
            setClientId(id);
            setClientName(c ? (c.Title || c.name || '') : '');
          }}
          style={{
            width: '100%', padding: '14px 12px',
            background: 'var(--bg-1)', color: 'var(--ink)',
            border: '1px solid var(--rule-2)', fontSize: 14,
            fontFamily: 'var(--font-sans)', marginBottom: 32,
          }}
        >
          <option value="">— Choisir un client —</option>
          {/* Client de la facture absent de la liste (fiche supprimée) : on le conserve. */}
          {isEdit && clientName && !clients.some(c => c.id === clientId) && (
            <option value={clientId}>{clientName}</option>
          )}
          {clients.map(c => <option key={c.id} value={c.id}>{c.Title || c.name}</option>)}
        </select>

        <div className="eyebrow" style={{ marginBottom: 16 }}>§ 02 · Date de prestation</div>
        <input
          type="date"
          value={prestaDate}
          onChange={(e) => setPrestaDate(e.target.value)}
          style={{
            padding: '12px', background: 'var(--bg-1)', color: 'var(--ink)',
            border: '1px solid var(--rule-2)', fontFamily: 'var(--font-mono)', marginBottom: 32,
          }}
        />

        <div className="eyebrow" style={{ marginBottom: 16 }}>§ 03 · Prestations</div>
        <div style={{ borderTop: '1px solid var(--rule-strong)', marginBottom: 20 }}>
          <div style={{
            display: 'grid', gridTemplateColumns: LINE_COLS,
            gap: 12, padding: '10px 0', fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--ink-3)',
            letterSpacing: '0.14em', textTransform: 'uppercase', borderBottom: '1px solid var(--rule)',
          }}>
            <div>N°</div><div>Description</div>
            <div style={{ textAlign: 'center' }}>Nature</div>
            <div style={{ textAlign: 'right' }}>Qté</div>
            <div style={{ textAlign: 'right' }}>PU HT</div>
            <div style={{ textAlign: 'right' }}>Total</div>
            <div></div>
          </div>
          {products.map((p, i) => {
            const rowTotal = (Number(p.quantity) || 0) * parsePrice(p.unitPrice);
            return (
              <div key={i} style={{
                display: 'grid', gridTemplateColumns: LINE_COLS,
                gap: 12, padding: '14px 0', borderBottom: '1px solid var(--rule)', alignItems: 'center',
              }}>
                <div className="mono" style={{ color: 'var(--ink-4)' }}>{String(i + 1).padStart(2, '0')}</div>
                <input
                  placeholder="Libellé de la prestation"
                  value={p.title}
                  onChange={(e) => updateP(i, 'title', e.target.value)}
                  style={{ background: 'transparent', border: 0, borderBottom: '1px dashed var(--rule-2)', color: 'var(--ink)', padding: '8px 0', outline: 'none', fontSize: 13 }}
                />
                <button
                  type="button"
                  onClick={() => updateP(i, 'isMerchandise', !p.isMerchandise)}
                  title="Bascule prestation ↔ marchandise (taux URSSAF différent)"
                  className="mono"
                  style={{
                    fontSize: 9.5, letterSpacing: '0.12em', textTransform: 'uppercase',
                    padding: '5px 0', borderRadius: 3, cursor: 'pointer',
                    border: `1px solid ${p.isMerchandise ? 'color-mix(in oklch, var(--warn), transparent 50%)' : 'var(--rule-2)'}`,
                    color: p.isMerchandise ? 'var(--warn)' : 'var(--ink-3)',
                    background: 'transparent',
                  }}
                >
                  {p.isMerchandise ? 'marchand.' : 'presta'}
                </button>
                <input
                  type="number"
                  value={p.quantity}
                  onChange={(e) => updateP(i, 'quantity', e.target.value)}
                  style={{ background: 'transparent', border: 0, borderBottom: '1px dashed var(--rule-2)', color: 'var(--ink)', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 13, outline: 'none' }}
                />
                <input
                  type="number"
                  step="0.01"
                  value={p.unitPrice}
                  onChange={(e) => updateP(i, 'unitPrice', e.target.value)}
                  style={{ background: 'transparent', border: 0, borderBottom: '1px dashed var(--rule-2)', color: 'var(--ink)', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 13, outline: 'none' }}
                />
                <div className="mono tnum" style={{ textAlign: 'right', color: 'var(--accent)' }}>{fmtEUR(rowTotal)} €</div>
                <button
                  type="button"
                  onClick={() => setProducts(products.filter((_, j) => j !== i))}
                  style={{ color: 'var(--ink-4)', background: 'transparent', border: 0, cursor: 'pointer' }}
                  aria-label="Supprimer la ligne"
                >
                  ×
                </button>
              </div>
            );
          })}
          <button
            type="button"
            onClick={() => setProducts([...products, { title: '', description: '', quantity: 1, unitPrice: 0, isMerchandise: false }])}
            style={{ display: 'block', width: '100%', padding: '14px 0', color: 'var(--ink-4)', background: 'transparent', border: 0, fontFamily: 'var(--font-mono)', fontSize: 12, letterSpacing: '0.08em', cursor: 'pointer' }}
          >
            ＋ ajouter une ligne
          </button>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 32 }}>
          <div style={{ width: 360 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--rule)', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--ink-2)' }}>
              <span>Sous-total HT</span>
              <span className="mono tnum">{fmtEUR(total)} €</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--rule)', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--ink-2)' }}>
              <span>TVA (0% · art. 293 B)</span>
              <span className="mono tnum">—</span>
            </div>
            <div style={{
              marginTop: 8, paddingTop: 18, borderTop: '2px solid var(--ink)',
              display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
            }}>
              <span className="mono" style={{ fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase' }}>Total à payer</span>
              <span className="mono tnum" style={{ fontSize: 28, fontWeight: 500, color: 'var(--accent)' }}>{fmtEUR(total)} €</span>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <Btn kind="ghost" onClick={onClose}>Annuler</Btn>
          <Btn kind="primary" icon={isEdit ? 'check' : undefined} onClick={submit} disabled={saving}>
            {saving ? 'Enregistrement…' : isEdit ? 'Enregistrer les modifications' : 'Émettre la facture'}
          </Btn>
        </div>
      </div>
    </div>
  );
}

// --------- Schedule (échéancier) Form ---------
function ScheduleForm({ clients, onClose, onSubmit }) {
  const [clientId, setClientId] = useState('');
  const [clientName, setClientName] = useState('');
  const [title, setTitle] = useState('');
  const [price, setPrice] = useState(0);
  const [start, setStart] = useState(new Date().toISOString().slice(0, 10));
  const [end, setEnd] = useState(new Date().toISOString().slice(0, 10));
  const [submitting, setSubmitting] = useState(false);

  // Nombre d'échéances (une facture par mois entre les deux dates)
  const count = useMemo(() => {
    const s = new Date(start);
    const e = new Date(end);
    if (isNaN(s.getTime()) || isNaN(e.getTime()) || e < s) return 0;
    let n = 0;
    for (let d = new Date(s); d <= e; d.setMonth(d.getMonth() + 1)) n++;
    return n;
  }, [start, end]);

  const totalAmount = count * (parsePrice(price) || 0);

  const inputStyle = {
    width: '100%', padding: '12px', background: 'var(--bg-1)', color: 'var(--ink)',
    border: '1px solid var(--rule-2)', fontFamily: 'var(--font-sans)', fontSize: 14,
  };

  const handleSubmit = async () => {
    if (!clientId) { alert('Sélectionner un client'); return; }
    if (!title.trim()) { alert('Saisir un titre de prestation'); return; }
    if (count === 0) { alert('Vérifiez les dates : la date de fin doit être après la date de début.'); return; }
    setSubmitting(true);
    try {
      await onSubmit({ clientId, clientName, title: title.trim(), price: parsePrice(price) || 0, start, end });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="ta-drawer-scrim" onClick={onClose}>
      <div className="ta-drawer-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 32 }}>
          <div>
            <div className="eyebrow" style={{ color: 'var(--accent)' }}>§ 02.B · Échéancier</div>
            <div style={{ fontSize: 32, letterSpacing: '-0.02em', marginTop: 8 }}>Créer un échéancier</div>
          </div>
          <button onClick={onClose} className="ta-icon-btn" aria-label="Fermer">{Ic.close}</button>
        </div>

        <div className="eyebrow" style={{ marginBottom: 16 }}>§ 01 · Client</div>
        <select
          value={clientId}
          onChange={(e) => {
            const id = e.target.value;
            const c = clients.find(x => x.id === id);
            setClientId(id);
            setClientName(c ? (c.Title || c.name || '') : '');
          }}
          style={{ ...inputStyle, marginBottom: 32 }}
        >
          <option value="">— Choisir un client —</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.Title || c.name}</option>)}
        </select>

        <div className="eyebrow" style={{ marginBottom: 16 }}>§ 02 · Prestation</div>
        <input
          placeholder="Titre de la prestation"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          style={{ ...inputStyle, marginBottom: 20 }}
        />
        <input
          type="number"
          step="0.01"
          placeholder="Montant mensuel HT (€)"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          style={{ ...inputStyle, fontFamily: 'var(--font-mono)', marginBottom: 32 }}
        />

        <div className="eyebrow" style={{ marginBottom: 16 }}>§ 03 · Période</div>
        <div style={{ display: 'flex', gap: 16, marginBottom: 32, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: '0.12em', marginBottom: 8, textTransform: 'uppercase' }}>Début</div>
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)} style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} />
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div className="mono" style={{ fontSize: 10, color: 'var(--ink-3)', letterSpacing: '0.12em', marginBottom: 8, textTransform: 'uppercase' }}>Fin</div>
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} />
          </div>
        </div>

        <div style={{
          border: '1px solid var(--rule)', padding: 20, marginBottom: 32,
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        }}>
          <div className="mono" style={{ fontSize: 12, color: 'var(--ink-2)' }}>
            {count} facture{count > 1 ? 's' : ''} mensuelle{count > 1 ? 's' : ''}
          </div>
          <div className="mono tnum" style={{ fontSize: 24, color: 'var(--accent)' }}>{fmtEUR(totalAmount)} €</div>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <Btn kind="ghost" onClick={onClose}>Annuler</Btn>
          <Btn kind="primary" onClick={handleSubmit}>
            {submitting ? 'Génération…' : 'Créer & télécharger'}
          </Btn>
        </div>
      </div>
    </div>
  );
}

export default Invoices;
